import React, { useState, useRef, useEffect } from 'react';
import { UserRole } from '../../utils/enums';

// Department options matching the backend
const DEPARTMENT_OPTIONS = [
  {
    value: 'property_management',
    label: 'Property Management',
    description: 'Property Manager and Maintenance roles',
    roles: [UserRole.PROPERTY_MANAGER, UserRole.MAINTENANCE],
    color: 'blue'
  },
  {
    value: 'housekeeping',
    label: 'Housekeeping',
    description: 'Housekeeper Manager role',
    roles: [UserRole.HOUSEKEEPER_MANAGER],
    color: 'green'
  }
];

function WorkOrderDepartmentFilter({ selectedDepartments, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggleDepartment = (deptValue) => {
    if (selectedDepartments.includes(deptValue)) {
      onChange(selectedDepartments.filter(d => d !== deptValue));
    } else {
      onChange([...selectedDepartments, deptValue]);
    }
  };

  const handleSelectAll = () => {
    onChange(DEPARTMENT_OPTIONS.map(d => d.value));
  };

  const handleClearAll = () => {
    onChange([]);
  };

  const selectedDepartmentObjects = DEPARTMENT_OPTIONS.filter(dept => 
    selectedDepartments.includes(dept.value)
  );

  // Convert department values to role strings for backend
  const getRolesFromDepartments = (departments) => {
    const roles = [];
    departments.forEach(deptValue => {
      const dept = DEPARTMENT_OPTIONS.find(d => d.value === deptValue);
      if (dept) {
        roles.push(...dept.roles);
      }
    });
    return roles;
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        Department
      </label>
      
      {/* Dropdown button */}
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
        >
          <span className="flex items-center">
            {selectedDepartmentObjects.length === 0 ? (
              <span className="text-gray-500">All departments</span>
            ) : (
              <span className="flex items-center space-x-1">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                  {selectedDepartmentObjects[0].label}
                </span>
                {selectedDepartmentObjects.length > 1 && (
                  <span className="text-gray-500">+{selectedDepartmentObjects.length - 1}</span>
                )}
              </span>
            )}
          </span>
          <svg
            className={`h-5 w-5 text-gray-400 transform transition-transform ${isOpen ? 'rotate-180' : ''}`}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>

        {/* Dropdown menu */}
        {isOpen && (
          <div className="absolute z-50 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
            {/* Quick action buttons */}
            <div className="border-b border-gray-200 px-3 py-2">
              <div className="flex justify-between gap-2">
                <button
                  type="button"
                  onClick={handleClearAll}
                  className="flex-1 px-2 py-1 text-xs font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded"
                >
                  Clear All
                </button>
                <button
                  type="button"
                  onClick={handleSelectAll}
                  className="flex-1 px-2 py-1 text-xs font-medium text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded"
                >
                  Select All
                </button>
              </div>
            </div>

            {/* Department options */}
            {DEPARTMENT_OPTIONS.map(department => (
              <div
                key={department.value}
                className={`cursor-pointer select-none relative py-3 pl-3 pr-9 hover:bg-gray-100 ${
                  selectedDepartments.includes(department.value) ? 'bg-purple-50' : ''
                }`}
                onClick={() => handleToggleDepartment(department.value)}
              >
                <div className="flex items-center">
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">{department.label}</div>
                    <div className="text-xs text-gray-500 mt-1">{department.description}</div>
                  </div>
                  {selectedDepartments.includes(department.value) && (
                    <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-purple-600">
                      <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Selected department tags */}
      {selectedDepartmentObjects.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedDepartmentObjects.map(department => (
            <span
              key={department.value}
              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800"
            >
              {department.label}
              <button
                type="button"
                onClick={() => handleToggleDepartment(department.value)}
                className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-white hover:bg-opacity-20"
              >
                <span className="sr-only">Remove {department.label}</span>
                <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Helper text */}
      <p className="text-xs text-gray-500">
        {selectedDepartments.length === 0
          ? 'Showing work orders from all departments'
          : `Filtered to ${selectedDepartments.length} department${selectedDepartments.length !== 1 ? 's' : ''}`
        }
      </p>
    </div>
  );
}

export { DEPARTMENT_OPTIONS };
export default WorkOrderDepartmentFilter;

